import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parseISO } from 'date-fns';
import { Save, Send, CalendarIcon } from 'lucide-react';
import { axios, auth, mbUrl, BRANCH_LABEL, muted, primary, runCall, toast, store } from './mbApi';
import { PdfImport } from './PdfImport';
import { BriefImages, RefWarning } from './BriefRefs';

const EMPTY = { title: '', branch: 'ai_self', deadline: '', audience_problem: '', angle: '', hook: '', offer: '', cta: '', hypothesis: '', refs: '', videos: '', brief: '', brief_pdf_url: '' };
export const FIELDS = [
  ['audience_problem', 'Masalah audiens'], ['angle', 'Angle mesej'], ['hook', 'Hook'],
  ['offer', 'Offer'], ['cta', 'CTA'], ['hypothesis', 'Hipotesis — kenapa nak test ni?'],
];
const LONG = new Set(['audience_problem', 'hook', 'hypothesis']);
const LABEL = { title: 'Tajuk', ...Object.fromEntries(FIELDS), refs: 'Link rujukan', videos: 'Video rujukan', brief: 'Nota', deadline: 'Deadline' };

export const DeadlinePicker = ({ value, onChange, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button id={id} variant="outline" className="justify-start font-normal h-10 w-full" data-testid="mb-workflow-deadline">
          <CalendarIcon size={14} className="mr-2" />{value ? format(parseISO(value), 'd MMM yyyy') : 'Pilih tarikh (pilihan)'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[hsl(var(--card))] border-[hsl(var(--border))]" align="start">
        <Calendar mode="single" selected={value ? parseISO(value) : undefined}
          onSelect={(d) => { onChange(d ? format(d, 'yyyy-MM-dd') : ''); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
};

const Field = ({ id, label, hint, children }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className={`text-sm ${primary}`}>{label}</Label>
    {children}
    {hint && <p className={`text-xs ${muted}`}>{hint}</p>}
  </div>
);

const ConflictBanner = ({ pending, apply, keep }) => (
  <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm space-y-2" data-testid="mb-pdf-conflict">
    <p className="text-amber-400">PDF ada nilai berbeza untuk ruangan yang anda sudah isi: {Object.keys(pending).map((k) => LABEL[k] || k).join(', ')}.</p>
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" className="h-10" onClick={apply} data-testid="mb-pdf-conflict-apply">Guna nilai PDF</Button>
      <Button size="sm" variant="ghost" className="h-10" onClick={keep} data-testid="mb-pdf-conflict-keep">Kekalkan isi saya</Button>
    </div>
  </div>
);

const loadSaved = (key) => {
  try { return JSON.parse(store.get(key) || 'null'); } catch { return null; }
};

export const BriefForm = ({ ibId, onCreated, draft, onDraftDone }) => {
  const key = `mb-brief-draft-${ibId}`;
  const [saved] = useState(() => (draft ? null : loadSaved(key)));
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(saved?.form || {}) }));
  const [images, setImages] = useState(() => saved?.images || []);
  const [sid, setSid] = useState(() => saved?.sid || '');
  const [busy, setBusy] = useState(false);
  const [pdfKey, setPdfKey] = useState(0);
  const [tried, setTried] = useState(false);
  const [pending, setPending] = useState(null);
  const [pdfWarn, setPdfWarn] = useState([]);
  useEffect(() => {
    if (!draft) return;
    const f = draft.fields || {};
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(f).filter(([k, v]) => k in EMPTY && typeof v === 'string')),
      branch: draft.branch === 'creative_team' ? 'creative_team' : 'ai_self' });
    setSid(draft.suggestionId);
  }, [draft]);
  useEffect(() => {
    const dirty = Object.keys(EMPTY).some((k) => form[k] !== EMPTY[k]) || images.length > 0;
    store.set(key, dirty ? JSON.stringify({ form, images, sid }) : '');
  }, [form, images, sid, key]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const reset = () => { setForm(EMPTY); setImages([]); setSid(''); setTried(false); setPending(null); setPdfWarn([]); setPdfKey((k) => k + 1); store.set(key, ''); };
  const missing = FIELDS.filter(([k]) => !form[k].trim()).map(([, l]) => l.split(' —')[0]);

  const submit = (status) => {
    setTried(true);
    if (!form.title.trim()) { toast.error('Tajuk brief diperlukan'); return; }
    runCall(setBusy, async () => {
      const { refs, videos, ...rest } = form;
      const split = (t) => t.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      let data;
      try {
        ({ data } = await axios.post(mbUrl(ibId, '/workflows'), {
          ...rest, reference_links: split(refs), video_links: split(videos), ref_image_ids: images, status,
          ...(sid ? { source_suggestion_id: sid } : {}),
        }, auth()));
      } catch (e) {
        if (e.response?.status !== 409 || !sid) throw e;
        toast.message('Brief dari cadangan ni sedang dicipta — papan kerja dimuat semula');
        onCreated();
        return;
      }
      if (data.existing) toast.message(`Cadangan ni dah jadi brief ${data.workflow?.code || ''} — tiada brief baharu dicipta`);
      else if (status === 'brief_sedia' && missing.length) toast.message(`Brief disimpan. Masih kosong: ${missing.join(', ')}`);
      if (data.workflow?.code) toast.success(`Kod brief: ${data.workflow.code}`);
      onDraftDone?.();
      reset();
      onCreated(data.workflow);
    }, status === 'kajian' ? 'Draf brief disimpan (status Kajian)' : 'Brief sedia untuk diserah');
  };
  const fillFromPdf = (f, src, imgs, warnings) => {
    const incoming = Object.fromEntries(Object.entries({
      ...Object.fromEntries(Object.entries(f).filter(([k, v]) => k in EMPTY && typeof v === 'string')),
      refs: (f.reference_links || []).join('\n'), videos: (f.video_links || []).join('\n'),
    }).filter(([, v]) => v && v.trim()));
    const conflicts = Object.fromEntries(Object.entries(incoming).filter(([k, v]) => form[k]?.trim() && form[k].trim() !== v.trim()));
    setForm((p) => ({ ...p, ...Object.fromEntries(Object.entries(incoming).filter(([k]) => !p[k]?.trim())), brief_pdf_url: src }));
    setImages((imgs || []).map((i) => i.id));
    setPdfWarn(warnings || []);
    setPending(Object.keys(conflicts).length ? conflicts : null);
  };

  return (
    <div className="space-y-4" data-testid="mb-brief-form">
      {draft && (
        <div className="rounded-lg border border-sky-500/40 bg-sky-500/5 p-3 text-sm text-sky-300 flex flex-wrap items-center gap-2" data-testid="mb-draft-banner">
          <span className="flex-1">Draf brief dari cadangan AI &quot;{draft.label}&quot; (versi {draft.from}). BELUM disimpan — semak & ubah dulu.</span>
          <button className="underline min-h-10" onClick={() => { reset(); onDraftDone?.(); }} data-testid="mb-draft-cancel">Buang draf</button>
        </div>
      )}
      {saved && !draft && (
        <p className="text-sm text-sky-400 flex flex-wrap items-center gap-2" data-testid="mb-draft-restored">
          Draf anda dipulihkan dari peranti ini.
          <button className="underline min-h-10" onClick={reset} data-testid="mb-draft-clear">Kosongkan borang</button>
        </p>
      )}
      <PdfImport key={pdfKey} ibId={ibId} onFilled={fillFromPdf} />
      {form.brief_pdf_url && <p className="text-sm text-emerald-400" data-testid="mb-pdf-filled">Diisi dari PDF — semak setiap ruangan sebelum simpan.</p>}
      {pdfWarn.length > 0 && <ul className="text-sm text-amber-400 list-disc pl-5" data-testid="mb-pdf-warnings">{pdfWarn.map((x) => <li key={x}>{x}</li>)}</ul>}
      {pending && <ConflictBanner pending={pending} apply={() => { setForm((p) => ({ ...p, ...pending })); setPending(null); }} keep={() => setPending(null)} />}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-3">
        <Field id="mb-f-title" label="Tajuk brief (wajib)" hint={tried && !form.title.trim() ? 'Tajuk diperlukan untuk simpan.' : ''}>
          <Input id="mb-f-title" value={form.title} onChange={(e) => set('title', e.target.value)} className="h-10" data-testid="mb-workflow-title" aria-invalid={tried && !form.title.trim()} />
        </Field>
        <Field id="mb-f-branch" label="Cara hasilkan bahan">
          <Select value={form.branch} onValueChange={(v) => set('branch', v)}>
            <SelectTrigger id="mb-f-branch" className="h-10" data-testid="mb-workflow-branch"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(BRANCH_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field id="mb-f-deadline" label="Deadline"><DeadlinePicker id="mb-f-deadline" value={form.deadline} onChange={(v) => set('deadline', v)} /></Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <Field key={k} id={`mb-f-${k}`} label={label}>
            {LONG.has(k)
              ? <Textarea id={`mb-f-${k}`} rows={3} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`mb-brief-${k}`} />
              : <Input id={`mb-f-${k}`} className="h-10" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`mb-brief-${k}`} />}
          </Field>
        ))}
      </div>
      <details className="rounded-lg border border-[hsl(var(--border))] p-3" open>
        <summary className={`cursor-pointer text-sm font-semibold ${primary} min-h-8`}>Rujukan & nota</summary>
        <div className="mt-3 space-y-3">
          <Field id="mb-f-refs" label="Link rujukan brand / Ads Library (disyorkan)" hint="Satu link satu baris.">
            <Textarea id="mb-f-refs" value={form.refs} onChange={(e) => set('refs', e.target.value)} rows={2} data-testid="mb-brief-refs" />
          </Field>
          <Field id="mb-f-videos" label="Video rujukan (disyorkan untuk brief video)" hint="YouTube / TikTok / Reels / Drive — satu link satu baris.">
            <Textarea id="mb-f-videos" value={form.videos} onChange={(e) => set('videos', e.target.value)} rows={2} data-testid="mb-brief-videos" />
          </Field>
          <BriefImages ibId={ibId} ids={images} testid="mb-brief-form-images" />
          <RefWarning refs={!!form.refs.trim()} images={images.length > 0} videos={!!form.videos.trim()} />
          <Field id="mb-f-brief" label="Nota tambahan">
            <Textarea id="mb-f-brief" value={form.brief} onChange={(e) => set('brief', e.target.value)} rows={2} data-testid="mb-workflow-brief" />
          </Field>
        </div>
      </details>
      <p className={`text-sm ${muted}`} data-testid="mb-brief-checklist">
        Checklist: {FIELDS.length - missing.length}/{FIELDS.length} ruangan diisi{missing.length ? ` · belum: ${missing.join(', ')}` : ' · lengkap'}. Sistem hanya wajibkan tajuk; draf disimpan automatik di peranti ini.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="h-10" onClick={() => submit('kajian')} disabled={busy} data-testid="mb-workflow-save-draft">
          <Save size={15} className="mr-1.5" /> Simpan draf
        </Button>
        <Button className="h-10" onClick={() => submit('brief_sedia')} disabled={busy} data-testid="mb-workflow-add">
          <Send size={15} className="mr-1.5" /> Sedia untuk diserah
        </Button>
      </div>
    </div>
  );
};
